import React from 'react';
import { motion } from 'framer-motion';
import { DollarSign, Users, LifeBuoy, Package, Server, Activity } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
export function AdminOverview() {
  const {
    invoices,
    tickets,
    packages,
    staff,
    serverConnections
  } = useAppContext();
  const totalRevenue = invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.total, 0);
  const openTickets = tickets.filter(t => t.status === 'open' || t.status === 'in-progress').length;
  const activePackages = packages.filter(p => p.active).length;
  const stats = [{
    label: 'Total Revenue',
    value: `$${totalRevenue.toFixed(2)}`,
    icon: DollarSign,
    color: 'text-emerald-500',
    bg: 'bg-emerald-500/10'
  }, {
    label: 'Active Clients',
    value: '156',
    icon: Users,
    color: 'text-blue-500',
    bg: 'bg-blue-500/10'
  }, {
    label: 'Open Tickets',
    value: openTickets.toString(),
    icon: LifeBuoy,
    color: 'text-amber-500',
    bg: 'bg-amber-500/10'
  }, {
    label: 'Active Services',
    value: activePackages.toString(),
    icon: Package,
    color: 'text-purple-500',
    bg: 'bg-purple-500/10'
  }, {
    label: 'Staff Members',
    value: staff.length.toString(),
    icon: Users,
    color: 'text-fox-500',
    bg: 'bg-fox-500/10'
  }, {
    label: 'Connected Servers',
    value: serverConnections.length.toString(),
    icon: Server,
    color: 'text-cyan-500',
    bg: 'bg-cyan-500/10'
  }];
  return <div className="space-y-8" data-id="element-35">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-36">
        <h1 className="text-3xl font-heading font-bold text-white mb-2" data-id="element-37">
          Admin Overview
        </h1>
        <p className="text-slate-400" data-id="element-38">System status and quick statistics.</p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-39">
        {stats.map((stat, i) => <motion.div key={i} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: i * 0.1
      }} className="bg-slate-950 border border-slate-800 rounded-2xl p-6 flex items-center gap-4" data-id="element-40">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${stat.bg} ${stat.color}`} data-id="element-41">
              <stat.icon size={28} data-id="element-42" />
            </div>
            <div data-id="element-43">
              <p className="text-slate-400 text-sm font-medium mb-1" data-id="element-44">
                {stat.label}
              </p>
              <h3 className="text-2xl font-bold text-white" data-id="element-45">{stat.value}</h3>
            </div>
          </motion.div>)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-46">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="bg-slate-950 border border-slate-800 rounded-2xl p-6" data-id="element-47">
          <h2 className="text-xl font-heading font-bold text-white mb-6" data-id="element-48">
            Recent Invoices
          </h2>
          <div className="space-y-4" data-id="element-49">
            {invoices.slice(0, 5).map(inv => <div key={inv.id} className="flex items-center justify-between p-4 bg-slate-900 rounded-xl border border-slate-800" data-id="element-50">
                <div data-id="element-51">
                  <p className="text-white font-medium" data-id="element-52">{inv.clientName}</p>
                  <p className="text-sm text-slate-400" data-id="element-53">{inv.id}</p>
                </div>
                <div className="text-right" data-id="element-54">
                  <p className="text-white font-medium" data-id="element-55">
                    ${inv.total.toFixed(2)}
                  </p>
                  <span className={`text-xs px-2 py-1 rounded-full ${inv.status === 'paid' ? 'bg-emerald-500/10 text-emerald-500' : inv.status === 'pending' ? 'bg-amber-500/10 text-amber-500' : 'bg-red-500/10 text-red-500'}`} data-id="element-56">
                    {inv.status.toUpperCase()}
                  </span>
                </div>
              </div>)}
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="bg-slate-950 border border-slate-800 rounded-2xl p-6" data-id="element-57">
          <h2 className="text-xl font-heading font-bold text-white mb-6" data-id="element-58">
            Recent Tickets
          </h2>
          <div className="space-y-4" data-id="element-59">
            {tickets.slice(0, 5).map(ticket => <div key={ticket.id} className="flex items-center justify-between p-4 bg-slate-900 rounded-xl border border-slate-800" data-id="element-60">
                <div data-id="element-61">
                  <p className="text-white font-medium truncate max-w-[200px]" data-id="element-62">
                    {ticket.subject}
                  </p>
                  <p className="text-sm text-slate-400" data-id="element-63">{ticket.clientName}</p>
                </div>
                <div className="text-right" data-id="element-64">
                  <span className={`text-xs px-2 py-1 rounded-full mb-1 inline-block ${ticket.priority === 'high' || ticket.priority === 'critical' ? 'bg-red-500/10 text-red-500' : 'bg-blue-500/10 text-blue-500'}`} data-id="element-65">
                    {ticket.priority.toUpperCase()}
                  </span>
                  <p className="text-xs text-slate-500" data-id="element-66">
                    {new Date(ticket.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>)}
          </div>
        </motion.div>
      </div>
    </div>;
}